import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, ChevronRight, LandPlot, Loader2, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { GrSteps } from '../icons/GrSteps';

import { assetService } from '../../services/assetService';
import { requestPasRegionAdmission } from '../../services/pasAdmissionService';
import { pasHatchSessionService } from '../../services/pasHatchSessionService';
import type { Asset } from '../../types/domain';
import { buildAssetPreviewUrl } from '../../utils/mediaUrl';
import { isPasAssetType, isPasAnchorPracticeSequenceId, PAS_ANCHOR_PRACTICE_SEQUENCE_ID, readPasAnchorFromAssetMetadata, readPasAnchorPracticeItems, readPasWorkbenchRegionsFromAssetMetadata } from '../../utils/pasAsset';
import {
  mergePasPracticeLibraryItems,
  refreshPasPracticeLibrary,
  type PasPracticeLibraryItem,
} from '../../utils/pasPracticeLibrary';
import { resolveSubjectCodeFromCandidates } from '../../utils/subjectConfig';
import { PasPracticeDrawer } from './PasPracticeDrawer';
import { usePasSplitLayout } from './pasSplitLayout';
import { TearDropCornerHandle, type TearDropCorner } from './TearDropCornerHandle';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const MIN_REGION_PX = 24;
const GATE_HIT = 36;
const HANDLE_HIT = 28;
/** Math DRS skip hatch: keep region passed without forcing a hatchPrompt. */
const DRS_DEFAULT_GATE_THEME = '台阶练习';

function resolvePasGateThemePlaceholder(...candidates: unknown[]): string {
  // Assets often store 英语/语文/数学 labels — normalizeSubjectCode would always fall back to math.
  const code = resolveSubjectCodeFromCandidates(...candidates) ?? 'math';
  if (code === 'english') {
    return '例如：不规则动词过去式的条件反射拼写';
  }
  if (code === 'yuwen') {
    return '例如：形近字「辩/辨/辫」的听写条件反射';
  }
  return '例如：二次求导的口算条件反射';
}

type GateStatus = 'idle' | 'judging' | 'passed' | 'failed';

type Region = {
  id: string;
  /** Normalized against natural image size 0..1 */
  rectNormalized: { x: number; y: number; width: number; height: number };
  gateStatus: GateStatus;
  gateTheme?: string;
  themeKpId?: string;
  regionContent?: string;
  regionFeedback?: string;
  practiceDraftId?: string;
  practiceItems?: PasPracticeLibraryItem[];
};

type DraftRect = { x: number; y: number; width: number; height: number };

export type PasThemeParticleOption = {
  id: string;
  title: string;
  summary?: string;
};

export type PasAnnotationWorkbenchHandle = {
  undo: () => void;
  /** Flush pending canvas state to server, then re-hydrate from server. */
  reload: () => Promise<void>;
  /** Flush pending canvas state to server immediately. */
  flush: () => Promise<void>;
  /** Select / open a region after hydrate (list deep-link). */
  focusRegion: (regionId: string) => void;
  /** Live practice-library items across all regions (for print / export). */
  listPracticeLibraryItems: () => PasPracticeLibraryItem[];
  /** Clear all ladder-practice library items (anchor + regions); keeps crop boxes. */
  clearLadderPracticeLibrary: () => Promise<void>;
};

type PasAnnotationWorkbenchProps = {
  asset: Asset;
  assetId: string;
  selectMode: boolean;
  /** When omitted, defaults to type===pas. Pass true for ordinary photo assets. */
  enabled?: boolean;
  themeParticles?: PasThemeParticleOption[];
  /** Focus this region once after hydrate (ordinary-asset practice deep-link). */
  initialFocusRegionId?: string | null;
  /** Open the practice panel on mount (e.g. workspace 阶梯练习视图 tab). */
  initialPracticeOpen?: boolean;
  /** Sync deep-link ?regionId= when user switches crop in the practice panel. */
  onActiveRegionChange?: (regionId: string | null) => void;
  onCanUndoChange?: (canUndo: boolean) => void;
  onToast?: (message: string) => void;
  /** Keep parent asset.metadata in sync after canvas persist (prevents remount wipe). */
  onAssetUpdated?: (asset: Asset) => void;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function clipRectToStage(
  rect: DraftRect,
  stageW: number,
  stageH: number,
): DraftRect {
  const x1 = clamp(rect.x, 0, stageW);
  const y1 = clamp(rect.y, 0, stageH);
  const x2 = clamp(rect.x + rect.width, 0, stageW);
  const y2 = clamp(rect.y + rect.height, 0, stageH);
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

function cornerCursor(_corner: TearDropCorner): string {
  return 'grab';
}

function containSize(naturalW: number, naturalH: number, boxW: number, boxH: number) {
  if (naturalW <= 0 || naturalH <= 0 || boxW <= 0 || boxH <= 0) {
    return { width: 0, height: 0 };
  }
  const aspect = naturalW / naturalH;
  const boxAspect = boxW / boxH;
  if (aspect >= boxAspect) {
    return { width: boxW, height: boxW / aspect };
  }
  return { width: boxH * aspect, height: boxH };
}

function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) return key;
  } catch {
    // ignore
  }
  return '';
}

function isJudging(status: GateStatus) {
  return status === 'judging';
}

/** Geometry frozen only while admission is in-flight. Passed boxes can be re-selected to edit. */
function canEditGeometry(status: GateStatus) {
  return status !== 'judging';
}

export const PasAnnotationWorkbench = forwardRef<
  PasAnnotationWorkbenchHandle,
  PasAnnotationWorkbenchProps
>(function PasAnnotationWorkbench(
  {
    asset,
    assetId,
    selectMode,
    enabled,
    themeParticles = [],
    initialFocusRegionId = null,
    initialPracticeOpen = false,
    onActiveRegionChange,
    onCanUndoChange,
    onToast,
    onAssetUpdated,
  },
  ref,
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const layoutRootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const selectModeRef = useRef(selectMode);
  const location = useLocation();
  const gateThemePlaceholder = useMemo(
    () => resolvePasGateThemePlaceholder(asset?.subject, asset?.ai_category),
    [asset?.subject, asset?.ai_category],
  );
  const isMathDrs = useMemo(
    () => (resolveSubjectCodeFromCandidates(asset?.subject, asset?.ai_category) ?? 'math') === 'math',
    [asset?.subject, asset?.ai_category],
  );
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [regions, setRegions] = useState<Region[]>([]);
  const [anchorPracticeItems, setAnchorPracticeItems] = useState<PasPracticeLibraryItem[]>([]);
  const [draftRect, setDraftRect] = useState<DraftRect | null>(null);
  const draftRectRef = useRef<DraftRect | null>(null);
  const [themeModal, setThemeModal] = useState<{
    regionId: string;
    draftTheme: string;
    draftThemeKpId: string;
  } | null>(null);
  const [hoverCursor, setHoverCursor] = useState('default');
  const isWideLayout = usePasSplitLayout(layoutRootRef);
  const [activePracticeRegionId, setActivePracticeRegionId] = useState<string | null>(null);
  const [practiceDrawerOpen, setPracticeDrawerOpen] = useState(initialPracticeOpen);
  const [autoEnumerateDrs, setAutoEnumerateDrs] = useState(false);
  /** In select mode: which historical/new box is active for resize / move / delete. */
  const [editingRegionId, setEditingRegionId] = useState<string | null>(null);
  const editingRegionIdRef = useRef<string | null>(null);
  const historyRef = useRef<Region[][]>([]);
  const regionsRef = useRef<Region[]>([]);
  const anchorPracticeItemsRef = useRef<PasPracticeLibraryItem[]>([]);
  /** Count of setRegions that must not trigger PUT (hydrate). */
  const suppressPersistCountRef = useRef(1);
  const hydrateReadyRef = useRef(false);
  const lastGoodPracticeCountRef = useRef(0);
  const forceHydrateRef = useRef(false);
  const persistTimerRef = useRef<number | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const panRef = useRef<{ x: number; y: number } | null>(null);
  const drawRef = useRef<{
    originX: number;
    originY: number;
    pointerId: number;
  } | null>(null);
  const moveRef = useRef<{
    regionId: string;
    startLocalX: number;
    startLocalY: number;
    startRect: Region['rectNormalized'];
  } | null>(null);
  const resizeRef = useRef<{
    regionId: string;
    corner: TearDropCorner;
    startRect: { x: number; y: number; width: number; height: number };
  } | null>(null);

  const isPas = isPasAssetType(asset.type);
  const workbenchEnabled = enabled ?? isPas;
  const pasAnchor = useMemo(
    () => readPasAnchorFromAssetMetadata(asset.metadata ?? null),
    [asset.metadata],
  );
  const requiresThemeKpPick = !pasAnchor?.kpId;

  const stage = useMemo(
    () => containSize(naturalSize.width, naturalSize.height, canvasSize.width, canvasSize.height),
    [canvasSize.height, canvasSize.width, naturalSize.height, naturalSize.width],
  );

  useEffect(() => {
    selectModeRef.current = selectMode;
    if (selectMode) {
      panRef.current = null;
      setHoverCursor('crosshair');
    } else {
      setHoverCursor('grab');
      setEditingRegionId(null);
      editingRegionIdRef.current = null;
    }
  }, [selectMode]);

  useEffect(() => {
    editingRegionIdRef.current = editingRegionId;
  }, [editingRegionId]);

  useEffect(() => {
    regionsRef.current = regions;
    onCanUndoChange?.(historyRef.current.length > 0);
  }, [onCanUndoChange, regions]);

  const bumpSuppressPersist = useCallback(() => {
    suppressPersistCountRef.current += 1;
  }, []);

  // Hydrate canvas regions from latest server pasWorkbench (prop may be stale after HMR/remount).
  useEffect(() => {
    let cancelled = false;
    hydrateReadyRef.current = false;
    historyRef.current = [];
    onCanUndoChange?.(false);

    const countPractice = (list: Region[], anchorItems: PasPracticeLibraryItem[] = anchorPracticeItemsRef.current) => (
      anchorItems.length
      + list.reduce(
        (sum, region) => sum + (Array.isArray(region.practiceItems) ? region.practiceItems.length : 0),
        0,
      )
    );

    const applyLoaded = (
      metadata: Record<string, unknown> | null | undefined,
      source: 'prop' | 'server',
    ) => {
      const loadedAnchor = refreshPasPracticeLibrary(readPasAnchorPracticeItems(metadata ?? null));
      const loaded = readPasWorkbenchRegionsFromAssetMetadata(metadata ?? null).map((region) => ({
        ...region,
        gateStatus: region.gateStatus === 'judging' ? 'idle' as const : region.gateStatus,
        practiceItems: region.practiceItems
          ? refreshPasPracticeLibrary(region.practiceItems)
          : undefined,
      }));

      // Never apply an empty/stale prop snapshot before server responds — that caused library wipes.
      if (source === 'prop') {
        if (loaded.length === 0 || countPractice(loaded, loadedAnchor) === 0) {
          return;
        }
      }

      if (source === 'server' && !forceHydrateRef.current) {
        const local = regionsRef.current;
        const localPractice = countPractice(local);
        const loadedPractice = countPractice(loaded, loadedAnchor);
        if (localPractice > loadedPractice) {
          return;
        }
        if (local.length > loaded.length && localPractice > 0 && loadedPractice === 0) {
          return;
        }
        // Never downgrade anchor library on stale server reads.
        if (anchorPracticeItemsRef.current.length > loadedAnchor.length) {
          return;
        }
      }
      forceHydrateRef.current = false;

      const practiceTotal = countPractice(loaded, loadedAnchor);
      if (practiceTotal > 0) {
        lastGoodPracticeCountRef.current = practiceTotal;
      }
      bumpSuppressPersist();
      setAnchorPracticeItems(loadedAnchor);
      anchorPracticeItemsRef.current = loadedAnchor;
      setRegions(loaded);
      regionsRef.current = loaded;
    };

    void (async () => {
      try {
        const fresh = await assetService.getAsset(assetId);
        if (cancelled) return;
        applyLoaded((fresh.metadata ?? null) as Record<string, unknown> | null, 'server');
        // Only sync workbench metadata upward — never replace preview url/oss_id
        // (fresh signed URLs would remount/clear the preview and flash the canvas).
        if (fresh?.metadata && typeof fresh.metadata === 'object') {
          onAssetUpdated?.({
            ...asset,
            ...fresh,
            id: fresh.id || assetId,
            url: asset.url ?? fresh.url,
            oss_id: asset.oss_id ?? fresh.oss_id,
            metadata: fresh.metadata,
          });
        }
      } catch {
        // Fallback to prop only if it actually has workbench content.
        if (!cancelled) {
          applyLoaded((asset.metadata ?? null) as Record<string, unknown> | null, 'prop');
        }
      } finally {
        if (!cancelled) {
          // Ready for user edits. Do NOT bump suppress here — that used to eat the next save.
          hydrateReadyRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: assetId/reloadNonce gate
  }, [assetId, reloadNonce]);

  const buildPasWorkbenchPayload = useCallback((snapshot: Region[]) => (
    snapshot.map((region) => {
      const base: {
        id: string;
        rectNormalized: Region['rectNormalized'];
        gateStatus: Region['gateStatus'];
        gateTheme?: string;
        themeKpId?: string;
        regionContent?: string;
        regionFeedback?: string;
        practiceDraftId?: string;
        practiceItems?: PasPracticeLibraryItem[];
      } = {
        id: region.id,
        rectNormalized: { ...region.rectNormalized },
        gateStatus: region.gateStatus,
        gateTheme: region.gateTheme,
        themeKpId: region.themeKpId,
        regionContent: region.regionContent,
        regionFeedback: region.regionFeedback,
        practiceDraftId: region.practiceDraftId,
      };
      if (Array.isArray(region.practiceItems)) {
        base.practiceItems = region.practiceItems;
      }
      return base;
    })
  ), []);

  const persistSnapshot = useCallback(async (snapshot: Region[]) => {
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    if (!hydrateReadyRef.current) {
      throw new Error('标注尚未加载完成，请稍后再试');
    }
    if (!workbenchEnabled) return null;

    const sentAnchor = refreshPasPracticeLibrary([...anchorPracticeItemsRef.current]);
    const sentPracticeByRegionId = new Map(
      snapshot.map((region) => [
        region.id,
        refreshPasPracticeLibrary(region.practiceItems || []),
      ]),
    );

    const practiceCount = sentAnchor.length + snapshot.reduce(
      (sum, region) => sum + (Array.isArray(region.practiceItems) ? region.practiceItems.length : 0),
      0,
    );
    if (snapshot.length === 0 && sentAnchor.length === 0 && lastGoodPracticeCountRef.current > 0) {
      console.warn('[PAS] skip persist: refusing to wipe practice library');
      return null;
    }

    try {
      const saved = await assetService.savePasWorkbench({
        assetId,
        regions: buildPasWorkbenchPayload(snapshot),
        anchorPracticeItems: sentAnchor,
      });
      lastGoodPracticeCountRef.current = practiceCount;
      const savedMeta = (saved.metadata ?? null) as Record<string, unknown> | null;
      const loadedAnchor = mergePasPracticeLibraryItems(
        refreshPasPracticeLibrary(readPasAnchorPracticeItems(savedMeta)),
        sentAnchor,
      );
      const loaded = readPasWorkbenchRegionsFromAssetMetadata(savedMeta).map((region) => {
        const sentItems = sentPracticeByRegionId.get(region.id) || [];
        const mergedItems = mergePasPracticeLibraryItems(
          refreshPasPracticeLibrary(region.practiceItems || []),
          sentItems,
        );
        return {
          ...region,
          gateStatus: region.gateStatus === 'judging' ? 'idle' as const : region.gateStatus,
          practiceItems: mergedItems.length > 0 ? mergedItems : undefined,
        };
      });
      bumpSuppressPersist();
      bumpSuppressPersist();
      setAnchorPracticeItems(loadedAnchor);
      anchorPracticeItemsRef.current = loadedAnchor;
      setRegions(loaded);
      regionsRef.current = loaded;
      // Preserve preview identity fields to avoid signed-URL churn flashing the canvas.
      onAssetUpdated?.({
        ...saved,
        url: asset.url ?? saved.url,
        oss_id: asset.oss_id ?? saved.oss_id,
      });
      return saved;
    } catch (error) {
      onToast?.(error instanceof Error ? error.message : '保存标注失败');
      throw error;
    }
  }, [asset, assetId, buildPasWorkbenchPayload, bumpSuppressPersist, workbenchEnabled, onAssetUpdated, onToast]);

  const flushPersist = useCallback(async () => {
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    await persistSnapshot(regionsRef.current);
  }, [persistSnapshot]);

  // Debounced persist — QG / practice nodes survive reload.
  useEffect(() => {
    if (suppressPersistCountRef.current > 0) {
      suppressPersistCountRef.current -= 1;
      return;
    }
    if (!hydrateReadyRef.current) return;
    if (!workbenchEnabled) return;

    persistTimerRef.current = window.setTimeout(() => {
      persistTimerRef.current = null;
      void persistSnapshot(regionsRef.current);
    }, 700);

    return () => {
      if (persistTimerRef.current != null) {
        window.clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [assetId, workbenchEnabled, persistSnapshot, regions]);

  // Flush pending edits on leave — debounce cleanup alone used to drop the last PUT.
  useEffect(() => {
    return () => {
      if (!hydrateReadyRef.current || !workbenchEnabled) return;
      const snapshot = regionsRef.current;
      if (snapshot.length === 0 && anchorPracticeItemsRef.current.length === 0 && lastGoodPracticeCountRef.current === 0) return;
      void assetService
        .savePasWorkbench({
          assetId,
          regions: buildPasWorkbenchPayload(snapshot),
          anchorPracticeItems: anchorPracticeItemsRef.current,
        })
        .catch(() => {});
    };
  }, [assetId, buildPasWorkbenchPayload, workbenchEnabled]);

  useEffect(() => {
    let cancelled = false;
    setImgFailed(false);
    setImgSrc(null);
    setNaturalSize({ width: 0, height: 0 });

    void (async () => {
      const ossId = String(asset.oss_id || '').trim() || extractOssKeyFromUrl(asset.url);
      if (ossId) {
        try {
          const signed = await assetService.getSignedOssUrl(ossId);
          if (!cancelled && signed) {
            setImgSrc(signed.replace(/^http:\/\//i, 'https://'));
            return;
          }
        } catch {
          // fall through
        }
      }
      if (cancelled) return;
      const fromRaw = buildAssetPreviewUrl(asset.url);
      setImgSrc(fromRaw);
      setImgFailed(!fromRaw);
    })();

    return () => {
      cancelled = true;
    };
  }, [asset.oss_id, asset.url]);

  // Decode natural size independently of stage layout (avoids chicken-and-egg with contain).
  useEffect(() => {
    if (!imgSrc) {
      return undefined;
    }
    let cancelled = false;
    const probe = new window.Image();
    probe.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: probe.naturalWidth || 0,
        height: probe.naturalHeight || 0,
      });
    };
    probe.onerror = () => {
      if (!cancelled) setImgFailed(true);
    };
    probe.src = imgSrc;
    return () => {
      cancelled = true;
    };
  }, [imgSrc]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setCanvasSize({ width, height });
    });
    ro.observe(el);

    const onNativeWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.12 : 0.12;
      setScale((prev) => clamp(Number((prev + delta).toFixed(2)), MIN_SCALE, MAX_SCALE));
    };
    el.addEventListener('wheel', onNativeWheel, { passive: false });

    return () => {
      ro.disconnect();
      el.removeEventListener('wheel', onNativeWheel);
    };
  }, []);

  const pushHistory = useCallback(() => {
    historyRef.current = [
      ...historyRef.current,
      regionsRef.current.map((region) => ({
        ...region,
        rectNormalized: { ...region.rectNormalized },
      })),
    ].slice(-40);
  }, []);

  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    setRegions(prev);
    setDraftRect(null);
    onCanUndoChange?.(historyRef.current.length > 0);
  }, [onCanUndoChange]);

  const focusRegion = useCallback((regionId: string) => {
    const region = regionsRef.current.find((item) => item.id === regionId);
    if (!region) return;
    setEditingRegionId(regionId);
    editingRegionIdRef.current = regionId;
    if (region.gateStatus === 'passed') {
      setActivePracticeRegionId(regionId);
      setPracticeDrawerOpen(true);
      onActiveRegionChange?.(regionId);
    }
  }, [onActiveRegionChange]);

  const selectPracticeSequence = useCallback((sequenceId: string) => {
    if (isPasAnchorPracticeSequenceId(sequenceId)) {
      setActivePracticeRegionId(null);
      setEditingRegionId(null);
      editingRegionIdRef.current = null;
      setPracticeDrawerOpen(true);
      onActiveRegionChange?.(null);
      return;
    }
    focusRegion(sequenceId);
  }, [focusRegion, onActiveRegionChange]);

  const focusedInitialRegionRef = useRef<string | null>(null);
  useEffect(() => {
    focusedInitialRegionRef.current = null;
  }, [assetId]);
  useEffect(() => {
    const target = String(initialFocusRegionId || '').trim();
    if (!target || !hydrateReadyRef.current) return;
    if (focusedInitialRegionRef.current === target) return;
    if (!regionsRef.current.some((region) => region.id === target)) return;
    focusedInitialRegionRef.current = target;
    focusRegion(target);
  }, [focusRegion, initialFocusRegionId, reloadNonce, regions]);

  const stairsOpenedRef = useRef(false);
  useEffect(() => {
    stairsOpenedRef.current = false;
  }, [assetId]);
  useEffect(() => {
    if (stairsOpenedRef.current || !hydrateReadyRef.current) return;
    const params = new URLSearchParams(location.search);
    const onPracticePath = /\/practice\/?$/.test(location.pathname);
    if (params.get('openStairs') !== '1' && !onPracticePath && !initialPracticeOpen) return;
    const wanted = String(params.get('regionId') || '').trim();
    const list = regionsRef.current;
    const region = (wanted && list.find((item) => item.id === wanted)) || list[0] || null;
    stairsOpenedRef.current = true;
    if (region) {
      setActivePracticeRegionId(region.id);
      setEditingRegionId(region.id);
      editingRegionIdRef.current = region.id;
    }
    setPracticeDrawerOpen(true);
  }, [assetId, initialPracticeOpen, location.pathname, location.search, regions]);

  const clearLadderPracticeLibrary = useCallback(async () => {
    const hadPractice = (
      anchorPracticeItemsRef.current.length > 0
      || regionsRef.current.some((region) => (region.practiceItems?.length ?? 0) > 0)
    );
    if (!hadPractice) {
      onToast?.('当前没有阶梯练习题目可清除');
      return;
    }
    pushHistory();
    anchorPracticeItemsRef.current = [];
    setAnchorPracticeItems([]);
    const clearedRegions = regionsRef.current.map((region) => ({
      ...region,
      practiceItems: undefined,
    }));
    regionsRef.current = clearedRegions;
    setRegions(clearedRegions);
    lastGoodPracticeCountRef.current = 0;
    setPracticeDrawerOpen(false);
    setActivePracticeRegionId(null);
    onActiveRegionChange?.(null);
    try {
      await persistSnapshot(clearedRegions);
      onToast?.('已清除当前阶梯练习题目库');
    } catch {
      onToast?.('清除失败，请重试');
    }
  }, [onActiveRegionChange, onToast, persistSnapshot, pushHistory]);

  useImperativeHandle(ref, () => ({
    undo,
    flush: flushPersist,
    focusRegion,
    listPracticeLibraryItems: () => {
      const seen = new Set<string>();
      const items: PasPracticeLibraryItem[] = [];
      for (const item of refreshPasPracticeLibrary(anchorPracticeItemsRef.current)) {
        const id = String(item.id || '').trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        items.push(item);
      }
      for (const region of regionsRef.current) {
        const list = Array.isArray(region.practiceItems) ? region.practiceItems : [];
        for (const item of refreshPasPracticeLibrary(list)) {
          const id = String(item.id || '').trim();
          if (!id || seen.has(id)) continue;
          seen.add(id);
          items.push(item);
        }
      }
      return items;
    },
    reload: async () => {
      try {
        await flushPersist();
      } catch {
        // still reload from server
      }
      forceHydrateRef.current = true;
      setReloadNonce((n) => n + 1);
    },
    clearLadderPracticeLibrary,
  }), [clearLadderPracticeLibrary, flushPersist, focusRegion, undo]);

  const clientToLocal = useCallback((clientX: number, clientY: number) => {
    const stageEl = stageRef.current;
    if (!stageEl || stage.width <= 0 || stage.height <= 0) {
      return { x: 0, y: 0 };
    }
    const rect = stageEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return { x: 0, y: 0 };
    }
    // Map through rendered (already transformed) stage box — reliable with CSS scale/translate.
    return {
      x: ((clientX - rect.left) / rect.width) * stage.width,
      y: ((clientY - rect.top) / rect.height) * stage.height,
    };
  }, [stage.height, stage.width]);

  const openDrsForRegion = useCallback((regionId: string) => {
    const region = regionsRef.current.find((item) => item.id === regionId);
    if (!region || region.gateStatus === 'judging') return;

    let themeKpId = region.themeKpId || pasAnchor?.kpId || '';
    if (requiresThemeKpPick && !themeKpId && themeParticles.length === 1) {
      const only = themeParticles[0]!;
      themeKpId = only.id;
      setRegions((prev) => {
        const next = prev.map((item) => (
          item.id === regionId ? { ...item, themeKpId: only.id } : item
        ));
        regionsRef.current = next;
        return next;
      });
      onToast?.(`仅一颗知识颗粒，已自动关联：${only.title || only.id}`);
    }

    if (region.gateStatus !== 'passed' || !region.gateTheme) {
      setRegions((prev) => {
        const next = prev.map((item) => (
          item.id === regionId
            ? {
                ...item,
                gateStatus: 'passed' as const,
                gateTheme: item.gateTheme || DRS_DEFAULT_GATE_THEME,
                themeKpId: themeKpId || item.themeKpId,
                practiceDraftId: item.practiceDraftId || `drs-${regionId}`,
              }
            : item
        ));
        regionsRef.current = next;
        return next;
      });
      void flushPersist();
    }
    setActivePracticeRegionId(regionId);
    setPracticeDrawerOpen(true);
  }, [flushPersist, onToast, pasAnchor?.kpId, requiresThemeKpPick, themeParticles]);

  const openGate = useCallback((regionId: string) => {
    const region = regionsRef.current.find((item) => item.id === regionId);
    if (!region) return;
    // judging: wait; idle/failed: edit; passed: reopen practice node for review/edit
    if (region.gateStatus === 'judging') return;

    let themeKpId = region.themeKpId || pasAnchor?.kpId || '';
    if (requiresThemeKpPick && !themeKpId && themeParticles.length === 1) {
      const only = themeParticles[0]!;
      themeKpId = only.id;
      setRegions((prev) => {
        const next = prev.map((item) => (
          item.id === regionId ? { ...item, themeKpId: only.id } : item
        ));
        regionsRef.current = next;
        return next;
      });
      onToast?.(`仅一颗知识颗粒，已自动关联：${only.title || only.id}`);
    }

    if (isMathDrs) {
      setThemeModal({
        regionId,
        draftTheme: region.gateTheme === DRS_DEFAULT_GATE_THEME ? '' : (region.gateTheme || ''),
        draftThemeKpId: themeKpId,
      });
      return;
    }

    setThemeModal({
      regionId,
      draftTheme: region.gateTheme || '',
      draftThemeKpId: themeKpId,
    });
  }, [isMathDrs, onToast, pasAnchor?.kpId, requiresThemeKpPick, themeParticles]);

  const openPracticeDrawerForToolbar = useCallback(() => {
    const list = regionsRef.current;
    const isReady = (item: Region) => item.gateStatus === 'passed';
    const preferred = activePracticeRegionId
      ? list.find((item) => item.id === activePracticeRegionId && isReady(item))
      : null;
    const target = preferred || list.find(isReady);
    if (!target && !isMathDrs) {
      onToast?.('请先框选并铺台阶');
      return;
    }
    if (target) setActivePracticeRegionId(target.id);
    setPracticeDrawerOpen(true);
  }, [activePracticeRegionId, isMathDrs, onToast]);

  const hasPracticeNodes = useMemo(
    () => regions.some((region) => region.gateStatus === 'passed'),
    [regions],
  );

  const isSplitLayout = isWideLayout && practiceDrawerOpen;
  const showPracticeSide = practiceDrawerOpen;
  const practiceAsOverlay = practiceDrawerOpen && !isSplitLayout;

  useEffect(() => {
    if (!isWideLayout || !hasPracticeNodes) return;
    const list = regionsRef.current;
    const current = activePracticeRegionId
      ? list.find((item) => item.id === activePracticeRegionId && item.gateStatus === 'passed')
      : null;
    if (current) return;
    const target = list.find((item) => item.gateStatus === 'passed');
    if (target) {
      setActivePracticeRegionId(target.id);
      setPracticeDrawerOpen(true);
    }
  }, [activePracticeRegionId, hasPracticeNodes, isWideLayout, regions]);

  const closePracticeDrawer = useCallback(() => {
    setPracticeDrawerOpen(false);
    setAutoEnumerateDrs(false);
    // Keep activePracticeRegionId so the card stays highlighted after close.
  }, []);

  const updateRegionHatchPrompt = useCallback((regionId: string, hatchPrompt: string) => {
    setRegions((prev) => {
      const next = prev.map((region) => (
        region.id === regionId ? { ...region, gateTheme: hatchPrompt } : region
      ));
      regionsRef.current = next;
      return next;
    });
    void flushPersist();
  }, [flushPersist]);

  const updatePracticeLibrary = useCallback(async (sequenceId: string, practiceItems: PasPracticeLibraryItem[]) => {
    const refreshed = refreshPasPracticeLibrary(practiceItems);
    if (isPasAnchorPracticeSequenceId(sequenceId)) {
      anchorPracticeItemsRef.current = refreshed;
      setAnchorPracticeItems(refreshed);
      const saved = await persistSnapshot(regionsRef.current);
      if (!saved) {
        throw new Error('保存练习库失败');
      }
      return;
    }
    const snapshot = regionsRef.current.map((region) => (
      region.id === sequenceId
        ? { ...region, practiceItems: refreshed }
        : region
    ));
    if (!snapshot.some((region) => region.id === sequenceId)) {
      throw new Error('框选区域不存在，无法保存练习库');
    }
    regionsRef.current = snapshot;
    setRegions(snapshot);
    const saved = await persistSnapshot(snapshot);
    if (!saved) {
      throw new Error('保存练习库失败');
    }
  }, [persistSnapshot]);

  const buildPasWorkbenchPayloadRef = useRef(buildPasWorkbenchPayload);
  buildPasWorkbenchPayloadRef.current = buildPasWorkbenchPayload;
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  const updatePracticeLibraryRef = useRef(updatePracticeLibrary);
  updatePracticeLibraryRef.current = updatePracticeLibrary;

  const isMathDrsRef = useRef(isMathDrs);
  isMathDrsRef.current = isMathDrs;

  useEffect(() => {
    if (!workbenchEnabled || !assetId) {
      return undefined;
    }

    pasHatchSessionService.attachAssetPersist(assetId, {
      persistItems: (regionId, items) => {
        updatePracticeLibraryRef.current(regionId, items);
      },
      onToast: (message) => onToastRef.current?.(message),
    });

    const unsub = pasHatchSessionService.subscribeAsset(assetId, (snap) => {
      // Math DRS library is DB-authoritative; idle hatch cache must not clobber persisted items.
      if (isMathDrsRef.current && !snap.running) {
        return;
      }
      setRegions((prev) => {
        const index = prev.findIndex((region) => region.id === snap.regionId);
        if (index < 0) return prev;
        const current = prev[index]!;
        const currentItems = current.practiceItems || [];
        if (
          currentItems.length === snap.items.length
          && currentItems.every((item, i) => item.id === snap.items[i]?.id)
        ) {
          return prev;
        }
        const next = prev.map((region) => (
          region.id === snap.regionId
            ? { ...region, practiceItems: refreshPasPracticeLibrary(snap.items) }
            : region
        ));
        regionsRef.current = next;
        return next;
      });
    });

    return () => {
      unsub();
      let detachedRegions = regionsRef.current.map((region) => ({
        ...region,
        rectNormalized: { ...region.rectNormalized },
        practiceItems: region.practiceItems
          ? region.practiceItems.map((item) => ({ ...item }))
          : undefined,
      }));
      pasHatchSessionService.detachAssetPersist(assetId, {
        persistItems: async (regionId, items) => {
          detachedRegions = detachedRegions.map((region) => (
            region.id === regionId
              ? { ...region, practiceItems: refreshPasPracticeLibrary(items) }
              : region
          ));
          try {
            await assetService.savePasWorkbench({
              assetId,
              regions: buildPasWorkbenchPayloadRef.current(detachedRegions),
              anchorPracticeItems: anchorPracticeItemsRef.current,
            });
          } catch {
            // Best-effort detached persist.
          }
        },
      });
    };
  }, [assetId, workbenchEnabled]);

  const updateRegionVision = useCallback((
    regionId: string,
    vision: { regionContent: string; regionFeedback: string },
  ) => {
    setRegions((prev) => {
      const next = prev.map((region) => (
        region.id === regionId
          ? {
              ...region,
              regionContent: vision.regionContent || region.regionContent,
              regionFeedback: vision.regionFeedback || region.regionFeedback,
            }
          : region
      ));
      regionsRef.current = next;
      return next;
    });
    void flushPersist();
  }, [flushPersist]);

  const runAdmission = useCallback(async (
    regionId: string,
    theme: string,
    themeKpIdOverride?: string,
  ) => {
    const trimmed = theme.trim();
    if (!trimmed) {
      onToast?.('请先填写要加强的题目特点');
      return;
    }
    const sourceKpId = String(themeKpIdOverride || '').trim()
      || String(regionsRef.current.find((item) => item.id === regionId)?.themeKpId || '').trim()
      || String(pasAnchor?.kpId || '').trim();
    if (requiresThemeKpPick && !sourceKpId) {
      onToast?.('请先选择当前素材中的相关知识颗粒');
      return;
    }

    setRegions((prev) => {
      const next = prev.map((region) => (
        region.id === regionId
          ? {
              ...region,
              gateStatus: 'judging' as const,
              gateTheme: trimmed,
              themeKpId: sourceKpId || region.themeKpId,
              practiceDraftId: undefined,
            }
          : region
      ));
      regionsRef.current = next;
      return next;
    });

    const region = regionsRef.current.find((item) => item.id === regionId);
    if (!region) return;

    try {
      const result = await requestPasRegionAdmission({
        assetId,
        regionId,
        theme: trimmed,
        sourceKpId: sourceKpId || undefined,
        subject: asset?.subject,
        cropRectNormalized: region.rectNormalized,
      });

      if (!result.admitted) {
        setRegions((prev) => {
          const next = prev.map((item) => (
            item.id === regionId
              ? { ...item, gateStatus: 'failed' as const, practiceDraftId: undefined }
              : item
          ));
          regionsRef.current = next;
          return next;
        });
        void flushPersist();
        onToast?.('未通过加强判定，选框可继续编辑');
        return;
      }

      setRegions((prev) => {
        const next = prev.map((item) => (
          item.id === regionId
            ? {
                ...item,
                gateStatus: 'passed' as const,
                gateTheme: trimmed,
                practiceDraftId: result.practiceDraftId,
                regionContent: result.regionContent || item.regionContent,
                regionFeedback: result.regionFeedback || item.regionFeedback,
              }
            : item
        ));
        regionsRef.current = next;
        return next;
      });
      void flushPersist();
      if (result.regionFeedback) {
        onToast?.(result.regionFeedback);
      } else if (result.warning) {
        onToast?.(`已创建刻意练习节点（软警告：${result.warning}）`);
      } else {
        onToast?.('已创建刻意练习节点');
      }
      setActivePracticeRegionId(regionId);
      setPracticeDrawerOpen(true);
    } catch {
      // Soft gate: never block new incubation on transient failures.
      setRegions((prev) => {
        const next = prev.map((item) => (
          item.id === regionId
            ? {
                ...item,
                gateStatus: 'passed' as const,
                gateTheme: trimmed,
                practiceDraftId: `practice-draft-${regionId}-${Date.now()}`,
              }
            : item
        ));
        regionsRef.current = next;
        return next;
      });
      void flushPersist();
      onToast?.('实战标注分析服务异常，已放行新建孵化（软警告）');
      setActivePracticeRegionId(regionId);
      setPracticeDrawerOpen(true);
    }
  }, [asset, assetId, flushPersist, onToast, pasAnchor?.kpId, requiresThemeKpPick]);

  const confirmThemeModal = useCallback(() => {
    if (!themeModal) return;
    const { regionId, draftTheme, draftThemeKpId } = themeModal;
    setThemeModal(null);
    if (isMathDrs) {
      const trimmed = draftTheme.trim();
      if (trimmed || draftThemeKpId) {
        const next = regionsRef.current.map((item) => (
          item.id === regionId
            ? {
                ...item,
                gateTheme: trimmed || item.gateTheme,
                themeKpId: draftThemeKpId || item.themeKpId,
              }
            : item
        ));
        regionsRef.current = next;
        setRegions(next);
      }
      setAutoEnumerateDrs(true);
      openDrsForRegion(regionId);
      return;
    }
    void runAdmission(regionId, draftTheme, draftThemeKpId);
  }, [isMathDrs, openDrsForRegion, runAdmission, themeModal]);

  const removeRegion = useCallback((regionId: string) => {
    const region = regionsRef.current.find((item) => item.id === regionId);
    if (!region || isJudging(region.gateStatus)) return;

    const practiceCount = Array.isArray(region.practiceItems) ? region.practiceItems.length : 0;
    const hasPractice = region.gateStatus === 'passed' || practiceCount > 0 || Boolean(region.gateTheme);
    if (hasPractice) {
      const ok = window.confirm(
        practiceCount > 0
          ? `删除该选框将同时删除刻意练习节点及全部 ${practiceCount} 道练习题，确定删除？`
          : '删除该选框将同时删除刻意练习节点及其衍生内容，确定删除？',
      );
      if (!ok) return;
    }

    pushHistory();
    setRegions((prev) => prev.filter((item) => item.id !== regionId));
    if (editingRegionIdRef.current === regionId) {
      setEditingRegionId(null);
      editingRegionIdRef.current = null;
    }
    if (activePracticeRegionId === regionId) {
      setActivePracticeRegionId(null);
      setPracticeDrawerOpen(false);
    }
    void flushPersist();
  }, [activePracticeRegionId, flushPersist, pushHistory]);

  const hitGate = useCallback((localX: number, localY: number) => {
    for (let index = regions.length - 1; index >= 0; index -= 1) {
      const region = regions[index];
      // Land-plot only for idle/failed boxes (passed uses practice card).
      if (!region || region.gateStatus === 'passed' || isJudging(region.gateStatus)) continue;
      const left = region.rectNormalized.x * stage.width;
      const top = region.rectNormalized.y * stage.height;
      const width = region.rectNormalized.width * stage.width;
      const height = region.rectNormalized.height * stage.height;
      const cx = left + width + GATE_HIT / 2;
      const cy = top + height / 2;
      if (
        localX >= cx - GATE_HIT / 2
        && localX <= cx + GATE_HIT / 2
        && localY >= cy - GATE_HIT / 2
        && localY <= cy + GATE_HIT / 2
      ) {
        return region.id;
      }
    }
    return null;
  }, [regions, stage.height, stage.width]);

  const hitDelete = useCallback((localX: number, localY: number) => {
    if (!selectMode || !editingRegionId) return null;
    for (let index = regions.length - 1; index >= 0; index -= 1) {
      const region = regions[index];
      if (!region || region.id !== editingRegionId || !canEditGeometry(region.gateStatus)) continue;
      const left = region.rectNormalized.x * stage.width;
      const top = region.rectNormalized.y * stage.height;
      const width = region.rectNormalized.width * stage.width;
      const dx = localX - (left + width - 11);
      const dy = localY - (top + 11);
      if (dx * dx + dy * dy <= 16 * 16) {
        return region.id;
      }
    }
    return null;
  }, [editingRegionId, regions, selectMode, stage.height, stage.width]);

  const hitHandle = useCallback((localX: number, localY: number): { regionId: string; corner: TearDropCorner } | null => {
    if (!selectMode || !editingRegionId) return null;
    for (let index = regions.length - 1; index >= 0; index -= 1) {
      const region = regions[index];
      if (!region || region.id !== editingRegionId || !canEditGeometry(region.gateStatus)) continue;
      const left = region.rectNormalized.x * stage.width;
      const top = region.rectNormalized.y * stage.height;
      const width = region.rectNormalized.width * stage.width;
      const height = region.rectNormalized.height * stage.height;
      const right = left + width;
      const bottom = top + height;
      if (
        localX >= left - HANDLE_HIT && localX <= left
        && localY >= top - HANDLE_HIT && localY <= top
      ) {
        return { regionId: region.id, corner: 'nw' };
      }
      if (
        localX >= right && localX <= right + HANDLE_HIT
        && localY >= top - HANDLE_HIT && localY <= top
      ) {
        return { regionId: region.id, corner: 'ne' };
      }
      if (
        localX >= left - HANDLE_HIT && localX <= left
        && localY >= bottom && localY <= bottom + HANDLE_HIT
      ) {
        return { regionId: region.id, corner: 'sw' };
      }
      if (
        localX >= right && localX <= right + HANDLE_HIT
        && localY >= bottom && localY <= bottom + HANDLE_HIT
      ) {
        return { regionId: region.id, corner: 'se' };
      }
    }
    return null;
  }, [editingRegionId, regions, selectMode, stage.height, stage.width]);

  const hitRegion = useCallback((localX: number, localY: number) => {
    for (let index = regions.length - 1; index >= 0; index -= 1) {
      const region = regions[index];
      if (!region || !canEditGeometry(region.gateStatus)) continue;
      const left = region.rectNormalized.x * stage.width;
      const top = region.rectNormalized.y * stage.height;
      const width = region.rectNormalized.width * stage.width;
      const height = region.rectNormalized.height * stage.height;
      if (
        localX >= left
        && localX <= left + width
        && localY >= top
        && localY <= top + height
      ) {
        return region.id;
      }
    }
    return null;
  }, [regions, stage.height, stage.width]);

  const resolveHoverCursor = useCallback((localX: number, localY: number) => {
    if (!selectModeRef.current) {
      setHoverCursor('grab');
      return;
    }
    if (drawRef.current || moveRef.current || resizeRef.current) {
      return;
    }
    const handleHit = hitHandle(localX, localY);
    if (handleHit) {
      setHoverCursor(cornerCursor(handleHit.corner));
      return;
    }
    if (hitRegion(localX, localY)) {
      setHoverCursor('grab');
      return;
    }
    // Outside image or empty image area: keep crosshair for boxing (never grab).
    setHoverCursor('crosshair');
  }, [hitHandle, hitRegion]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();

    if (selectModeRef.current) {
      panRef.current = null;
    } else {
      drawRef.current = null;
      moveRef.current = null;
      resizeRef.current = null;
      draftRectRef.current = null;
      setDraftRect(null);
    }

    const local = clientToLocal(event.clientX, event.clientY);

    if (selectModeRef.current) {
      const gateId = hitGate(local.x, local.y);
      if (gateId) {
        openGate(gateId);
        return;
      }
      const deleteId = hitDelete(local.x, local.y);
      if (deleteId) {
        removeRegion(deleteId);
        return;
      }
      const handleHit = hitHandle(local.x, local.y);
      if (handleHit) {
        const region = regionsRef.current.find((item) => item.id === handleHit.regionId);
        if (!region) return;
        setEditingRegionId(handleHit.regionId);
        editingRegionIdRef.current = handleHit.regionId;
        pushHistory();
        const left = region.rectNormalized.x * stage.width;
        const top = region.rectNormalized.y * stage.height;
        const width = region.rectNormalized.width * stage.width;
        const height = region.rectNormalized.height * stage.height;
        resizeRef.current = {
          regionId: handleHit.regionId,
          corner: handleHit.corner,
          startRect: { x: left, y: top, width, height },
        };
        setHoverCursor(cornerCursor(handleHit.corner));
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      const moveId = hitRegion(local.x, local.y);
      if (moveId) {
        const region = regionsRef.current.find((item) => item.id === moveId);
        if (!region) return;
        setEditingRegionId(moveId);
        editingRegionIdRef.current = moveId;
        pushHistory();
        moveRef.current = {
          regionId: moveId,
          startLocalX: local.x,
          startLocalY: local.y,
          startRect: { ...region.rectNormalized },
        };
        setHoverCursor('grabbing');
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }

      // Empty canvas: deselect active box, then start a new region (on-image) or pan (off-image).
      setEditingRegionId(null);
      editingRegionIdRef.current = null;
      const onImage = local.x >= 0 && local.y >= 0 && local.x <= stage.width && local.y <= stage.height;
      if (!onImage) {
        panRef.current = { x: event.clientX, y: event.clientY };
        setHoverCursor('grabbing');
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }

      // Start boxing on image; draft is clipped to stage.
      drawRef.current = { originX: local.x, originY: local.y, pointerId: event.pointerId };
      const initial = clipRectToStage(
        { x: local.x, y: local.y, width: 0, height: 0 },
        stage.width,
        stage.height,
      );
      draftRectRef.current = initial;
      setDraftRect(initial);
      setHoverCursor('crosshair');
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    panRef.current = { x: event.clientX, y: event.clientY };
    setHoverCursor('grabbing');
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [
    clientToLocal,
    hitDelete,
    hitGate,
    hitHandle,
    hitRegion,
    openGate,
    pushHistory,
    removeRegion,
    stage.height,
    stage.width,
  ]);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const local = clientToLocal(event.clientX, event.clientY);

    if (selectModeRef.current && panRef.current) {
      const dx = event.clientX - panRef.current.x;
      const dy = event.clientY - panRef.current.y;
      panRef.current = { x: event.clientX, y: event.clientY };
      setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      return;
    }

    if (!selectModeRef.current && panRef.current) {
      const dx = event.clientX - panRef.current.x;
      const dy = event.clientY - panRef.current.y;
      panRef.current = { x: event.clientX, y: event.clientY };
      setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      return;
    }

    if (!selectModeRef.current) {
      setHoverCursor(panRef.current ? 'grabbing' : 'grab');
      return;
    }

    if (resizeRef.current && stage.width > 0 && stage.height > 0) {
      const { regionId, corner, startRect } = resizeRef.current;
      let left = startRect.x;
      let top = startRect.y;
      let right = startRect.x + startRect.width;
      let bottom = startRect.y + startRect.height;

      if (corner === 'nw' || corner === 'sw') {
        left = clamp(local.x, 0, right - MIN_REGION_PX);
      }
      if (corner === 'ne' || corner === 'se') {
        right = clamp(local.x, left + MIN_REGION_PX, stage.width);
      }
      if (corner === 'nw' || corner === 'ne') {
        top = clamp(local.y, 0, bottom - MIN_REGION_PX);
      }
      if (corner === 'sw' || corner === 'se') {
        bottom = clamp(local.y, top + MIN_REGION_PX, stage.height);
      }

      const nextW = right - left;
      const nextH = bottom - top;
      setRegions((prev) => prev.map((region) => (
        region.id === regionId
          ? {
              ...region,
              rectNormalized: {
                x: left / stage.width,
                y: top / stage.height,
                width: nextW / stage.width,
                height: nextH / stage.height,
              },
            }
          : region
      )));
      return;
    }

    if (moveRef.current && stage.width > 0) {
      const { regionId, startLocalX, startLocalY, startRect } = moveRef.current;
      const dx = (local.x - startLocalX) / stage.width;
      const dy = (local.y - startLocalY) / stage.height;
      const nextX = clamp(startRect.x + dx, 0, 1 - startRect.width);
      const nextY = clamp(startRect.y + dy, 0, 1 - startRect.height);
      setRegions((prev) => prev.map((region) => (
        region.id === regionId
          ? { ...region, rectNormalized: { ...region.rectNormalized, x: nextX, y: nextY } }
          : region
      )));
      return;
    }

    if (drawRef.current) {
      const { originX, originY } = drawRef.current;
      const raw = {
        x: Math.min(originX, local.x),
        y: Math.min(originY, local.y),
        width: Math.abs(local.x - originX),
        height: Math.abs(local.y - originY),
      };
      const next = clipRectToStage(raw, stage.width, stage.height);
      draftRectRef.current = next;
      setDraftRect(next);
      return;
    }

    resolveHoverCursor(local.x, local.y);
  }, [clientToLocal, resolveHoverCursor, stage.height, stage.width]);

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    panRef.current = null;
    moveRef.current = null;
    resizeRef.current = null;
    if (drawRef.current) {
      const draft = draftRectRef.current;
      drawRef.current = null;
      draftRectRef.current = null;
      setDraftRect(null);
      if (
        draft
        && draft.width >= MIN_REGION_PX
        && draft.height >= MIN_REGION_PX
        && stage.width > 0
        && stage.height > 0
      ) {
        pushHistory();
        const next: Region = {
          id: `${assetId}-region-${Date.now()}`,
          rectNormalized: {
            x: draft.x / stage.width,
            y: draft.y / stage.height,
            width: draft.width / stage.width,
            height: draft.height / stage.height,
          },
          gateStatus: 'idle',
          themeKpId: pasAnchor?.kpId
            || (requiresThemeKpPick && themeParticles.length === 1 ? themeParticles[0]!.id : undefined),
        };
        setRegions((prev) => [...prev, next]);
        setEditingRegionId(next.id);
        editingRegionIdRef.current = next.id;
        if (isMathDrs) {
          setThemeModal({
            regionId: next.id,
            draftTheme: '',
            draftThemeKpId: next.themeKpId || '',
          });
        } else if (requiresThemeKpPick) {
          if (themeParticles.length === 0) {
            onToast?.('当前素材还没有可关联的知识颗粒，请先完成知识颗粒提取');
          } else if (themeParticles.length === 1) {
            const only = themeParticles[0]!;
            onToast?.(`仅一颗知识颗粒，已自动关联：${only.title || only.id}`);
            setThemeModal({
              regionId: next.id,
              draftTheme: '',
              draftThemeKpId: only.id,
            });
          } else {
            setThemeModal({
              regionId: next.id,
              draftTheme: '',
              draftThemeKpId: '',
            });
          }
        }
      }
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const local = clientToLocal(event.clientX, event.clientY);
    resolveHoverCursor(local.x, local.y);
  }, [
    assetId,
    clientToLocal,
    onToast,
    pasAnchor?.kpId,
    pushHistory,
    requiresThemeKpPick,
    resolveHoverCursor,
    stage.height,
    stage.width,
    themeParticles,
    isMathDrs,
  ]);

  const onPointerLeave = useCallback(() => {
    if (drawRef.current || moveRef.current || resizeRef.current || panRef.current) {
      return;
    }
    setHoverCursor(selectModeRef.current ? 'crosshair' : 'grab');
  }, []);

  if (!workbenchEnabled) {
    return null;
  }

  const activeRegion = activePracticeRegionId
    ? regions.find((region) => region.id === activePracticeRegionId)
    : null;

  const practiceRegionSequences = isMathDrs
    ? [
      {
        id: PAS_ANCHOR_PRACTICE_SEQUENCE_ID,
        groupIndex: 0,
        label: '起手式展开',
        practiceItems: refreshPasPracticeLibrary(anchorPracticeItems),
      },
      ...regions.map((region, index) => ({
        id: region.id,
        groupIndex: index + 1,
        label: String(region.gateTheme || '').trim() || `框选 ${index + 1}`,
        practiceItems: refreshPasPracticeLibrary(region.practiceItems || []),
      })),
    ]
    : regions.map((region, index) => ({
      id: region.id,
      groupIndex: index,
      label: String(region.gateTheme || '').trim() || `框选 ${index + 1}`,
      practiceItems: refreshPasPracticeLibrary(region.practiceItems || []),
    }));

  const practiceDrawer = showPracticeSide ? (
    <PasPracticeDrawer
      key={assetId}
      presentation="page"
      embedded={isSplitLayout}
      open
      regionId={activePracticeRegionId}
      assetId={assetId}
      subject={resolveSubjectCodeFromCandidates(asset?.subject, asset?.ai_category) ?? asset?.subject}
      sourceKpId={
        activeRegion?.themeKpId
        || pasAnchor?.kpId
        || themeParticles[0]?.id
        || null
      }
      initialHatchPrompt={activeRegion?.gateTheme || ''}
      initialPracticeItems={
        activeRegion?.practiceItems
        ?? (isMathDrs ? anchorPracticeItems : undefined)
      }
      regionSequences={practiceRegionSequences}
      onSelectRegionSequence={selectPracticeSequence}
      cropRectNormalized={activeRegion?.rectNormalized}
      regionContent={activeRegion?.regionContent}
      regionFeedback={activeRegion?.regionFeedback}
      onClose={closePracticeDrawer}
      onHatchPromptChange={updateRegionHatchPrompt}
      onPracticeLibraryChange={updatePracticeLibrary}
      onRegionVisionChange={updateRegionVision}
      onToast={onToast}
      autoEnumerateDrs={autoEnumerateDrs}
    />
  ) : null;

  return (
    <div
      ref={layoutRootRef}
      className="relative flex h-full min-h-0 flex-1 flex-row bg-[#F8FAFD]"
    >
      <div
        className={
          'relative min-h-0 min-w-0 '
          + (isSplitLayout ? 'w-[38%] max-w-[40%] shrink-0 flex-none' : 'flex-1')
        }
      >
        <div
          ref={viewportRef}
          className="relative h-full min-h-0 touch-none overflow-hidden"
          style={{ cursor: hoverCursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerLeave}
        >
        {imgFailed || !imgSrc ? (
          <div className="flex h-full items-center justify-center text-sm text-muted">
            {imgFailed ? '暂无预览' : '加载图片中…'}
          </div>
        ) : stage.width > 0 && canvasSize.width > 0 ? (
          <div
            ref={stageRef}
            className="pointer-events-none absolute left-1/2 top-1/2 origin-center will-change-transform"
            style={{
              width: stage.width,
              height: stage.height,
              marginLeft: -stage.width / 2,
              marginTop: -stage.height / 2,
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            }}
          >
            <img
              src={imgSrc}
              alt=""
              draggable={false}
              className="pointer-events-none absolute inset-0 h-full w-full select-none object-fill"
              onError={() => setImgFailed(true)}
            />

            {regions.map((region, index) => {
              const left = region.rectNormalized.x * stage.width;
              const top = region.rectNormalized.y * stage.height;
              const width = region.rectNormalized.width * stage.width;
              const height = region.rectNormalized.height * stage.height;
              const judging = isJudging(region.gateStatus);
              const editable = selectMode && canEditGeometry(region.gateStatus);
              const isEditing = editable && editingRegionId === region.id;
              const showGate = region.gateStatus === 'idle'
                || region.gateStatus === 'failed'
                || region.gateStatus === 'judging';
              const regionActive = activePracticeRegionId === region.id;
              return (
                <div
                  key={region.id}
                  className={`pointer-events-none absolute border-2 ${
                    region.gateStatus === 'passed'
                      ? regionActive || isEditing
                        ? 'border-solid border-primary bg-primary/15'
                        : 'border-dashed border-primary bg-primary/10'
                      : region.gateStatus === 'judging'
                        ? 'border-dashed border-primary/80 bg-primary/10'
                        : isEditing
                          ? 'border-solid border-[#2563eb] bg-[#2563eb]/25'
                          : 'border-dashed border-[#2563eb] bg-[#2563eb]/20'
                  }`}
                  style={{ left, top, width, height }}
                >
                  <span className="absolute left-0.5 top-0.5 rounded bg-[#2563eb] px-1 text-[11px] font-bold text-white">
                    {index + 1}
                  </span>
                  {isEditing ? (
                    <>
                      <TearDropCornerHandle corner="nw" color="#2563eb" />
                      <TearDropCornerHandle corner="ne" color="#2563eb" />
                      <TearDropCornerHandle corner="sw" color="#2563eb" />
                      <TearDropCornerHandle corner="se" color="#2563eb" />
                      <button
                        type="button"
                        className="pointer-events-auto absolute right-0.5 top-0.5 z-10 flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px] border-white bg-danger text-white"
                        aria-label="删除选框"
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          removeRegion(region.id);
                        }}
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </>
                  ) : null}
                  {showGate ? (
                    <button
                      type="button"
                      className="pointer-events-auto absolute top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-primary-strong bg-white shadow"
                      style={{ right: -(GATE_HIT / 2) - 2, cursor: judging ? 'default' : 'pointer' }}
                      aria-label={isMathDrs ? '铺台阶' : '孵化刻意练习'}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!judging) openGate(region.id);
                      }}
                    >
                      {region.gateStatus === 'judging' ? (
                        <Loader2 size={16} className="animate-spin text-primary-strong" />
                      ) : isMathDrs ? (
                        <GrSteps size={16} className="text-primary-strong" />
                      ) : (
                        <LandPlot size={16} className="text-primary-strong" strokeWidth={2.2} />
                      )}
                    </button>
                  ) : null}
                </div>
              );
            })}

            {draftRect ? (
              <div
                className="pointer-events-none absolute border-2 border-dashed border-[#2563eb] bg-[#2563eb]/20"
                style={{
                  left: draftRect.x,
                  top: draftRect.y,
                  width: draftRect.width,
                  height: draftRect.height,
                }}
              />
            ) : null}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="animate-spin text-primary" size={24} />
          </div>
        )}

        {selectMode ? (
          <div className="pointer-events-none absolute left-3 right-3 top-3 flex justify-center">
            <div className="inline-flex max-w-full items-center gap-2 rounded-lg border border-border bg-white/90 px-3 py-2 text-xs font-semibold text-ink shadow-sm">
              {isMathDrs
                ? <GrSteps size={14} className="shrink-0 text-primary-strong" />
                : <LandPlot size={14} className="shrink-0 text-primary-strong" strokeWidth={2.2} />}
              <span className="truncate">
                {isMathDrs ? '框选后确认即可生成阶梯练习。说明可选。' : '框选后填写分析主题，再判断是否适合刻意练习。'}
              </span>
            </div>
          </div>
        ) : null}
        </div>

        {!showPracticeSide ? (
        <div className="pointer-events-none absolute inset-y-0 right-0 z-20 flex items-center px-2">
          <div className="pointer-events-auto flex flex-col gap-1 rounded-xl border border-border/70 bg-surface/95 p-1 shadow-card backdrop-blur">
            <button
              type="button"
              onClick={openPracticeDrawerForToolbar}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-ink transition hover:border-primary-strong hover:bg-primary/5"
              aria-label={isMathDrs ? '打开阶梯练习' : '打开刻意练习'}
              title={isMathDrs ? '打开阶梯练习' : '打开刻意练习'}
            >
              <ChevronRight size={18} strokeWidth={2.2} />
            </button>
          </div>
        </div>
        ) : null}
      </div>

      {showPracticeSide ? (
        <div
          className={
            practiceAsOverlay
              ? 'absolute inset-0 z-10 flex min-h-0 flex-col bg-surface'
              : 'relative flex h-full min-h-0 min-w-0 flex-1 flex-col border-l border-border bg-surface'
          }
        >
          {practiceAsOverlay ? (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-20 flex items-center px-2">
              <div className="pointer-events-auto flex flex-col gap-1 rounded-xl border border-border/70 bg-surface/95 p-1 shadow-card backdrop-blur">
                <button
                  type="button"
                  onClick={closePracticeDrawer}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-ink transition hover:border-primary-strong hover:bg-primary/5"
                  aria-label="返回答题纸"
                  title="返回答题纸"
                >
                  <ChevronLeft size={18} strokeWidth={2.2} />
                </button>
              </div>
            </div>
          ) : null}
          {practiceDrawer}
        </div>
      ) : null}

      {themeModal ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/45 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="关闭"
            onClick={() => setThemeModal(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h2 className="text-base font-extrabold text-ink">
              {isMathDrs ? '生成阶梯练习' : '实战标注分析'}
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-muted">
              {isMathDrs
                ? '说明可选。不填也会按答题纸上的圈画生成阶梯练习；写清卡点会更准。'
                : '请填写本次实战标注分析的主题。被框选的内容会据此分析并判断是否适合生成刻意练习题目。'}
            </p>
            {requiresThemeKpPick ? (
              themeParticles.length === 1 && themeModal.draftThemeKpId ? (
                <p className="mt-3 rounded-xl border border-border bg-canvas px-3 py-2 text-xs leading-relaxed text-muted">
                  仅一颗知识颗粒，已自动关联：
                  <span className="font-semibold text-ink">
                    {themeParticles[0]?.title || themeParticles[0]?.id}
                  </span>
                </p>
              ) : (
                <label className="mt-3 block text-xs text-muted">
                  相关知识颗粒
                  <select
                    value={themeModal.draftThemeKpId}
                    onChange={(event) => {
                      setThemeModal((current) => (
                        current ? { ...current, draftThemeKpId: event.target.value } : current
                      ));
                    }}
                    className="mt-1 w-full rounded-xl border border-border bg-[#F8FAFD] px-3 py-2 text-sm text-ink outline-none focus:border-primary"
                  >
                    <option value="">请选择当前素材已提取的知识颗粒</option>
                    {themeParticles.map((particle) => (
                      <option key={particle.id} value={particle.id}>
                        {particle.title || particle.id}
                      </option>
                    ))}
                  </select>
                </label>
              )
            ) : null}
            <textarea
              autoFocus={!isMathDrs && !requiresThemeKpPick}
              value={themeModal.draftTheme}
              onChange={(event) => {
                setThemeModal((current) => (
                  current ? { ...current, draftTheme: event.target.value } : current
                ));
              }}
              placeholder={isMathDrs ? '可选：卡在哪一步、想从哪条路起手' : gateThemePlaceholder}
              className={
                'mt-3 min-h-[112px] w-full resize-y rounded-xl border px-3 py-2 text-sm outline-none focus:border-primary '
                + (isMathDrs
                  ? 'border-border/70 bg-canvas/60 text-ink/80'
                  : 'border-border bg-[#F8FAFD] text-ink')
              }
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg bg-canvas px-4 py-2 text-sm font-semibold text-ink hover:bg-black/5"
                onClick={() => setThemeModal(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                onClick={confirmThemeModal}
              >
                {isMathDrs ? '铺台阶' : '开始分析实战标注'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});
